// src/components/LoginBox.js
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthContext } from '../../contexts/AuthContext'; // ✅ use global auth (user, setUser, API_URL)
import './Login.css';

export default function LoginBox({ registrationSuccessMessage, onForgotPassword }) {
  const navigate = useNavigate();
  const { API_URL, setUser } = useAuthContext(); // ✅ get API_URL and setUser from context

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [showResendVerification, setShowResendVerification] = useState(false);
  const [resending, setResending] = useState(false);
  const [resendMessage, setResendMessage] = useState('');

  const handleLogin = async (event) => {
    event.preventDefault();
    if (isSubmitting) return;

    setIsSubmitting(true);
    setError(null);
    setShowResendVerification(false);
    setResendMessage('');

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000);

      // 1) Perform login
      const res = await fetch(`${API_URL}/api/login`, {
        method: 'POST',
        credentials: 'include',
        cache: 'no-store',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({ username, password }),
        signal: controller.signal,
        redirect: 'manual',
      });

      clearTimeout(timeoutId);

      if (!res.ok) {
        let msg = 'Login failed. Please try again.';
        let errorData = null;
        try {
          errorData = await res.json();
          msg = errorData?.error || msg;

          // Check if error is due to unverified email
          if (errorData?.code === 'EMAIL_NOT_VERIFIED') {
            setShowResendVerification(true);
          }
        } catch {
          try {
            msg = (await res.text()) || msg;
          } catch {
            msg = 'Network error.';
          }
        }
        throw new Error(msg);
      }

      // 2) Immediately fetch the current user and update global state
      //    This is the key step that triggers useTasks to refetch for the new user.
      try {
        const prof = await fetch(`${API_URL}/api/profile`, {
          credentials: 'include',
          headers: { Accept: 'application/json' },
        });
        if (prof.ok) {
          const userObj = await prof.json();
          setUser(userObj); // ✅ tell the app "the user changed"
        } else {
          // If /profile fails, at least ensure we don't keep stale user
          setUser(null);
        }
      } catch {
        setUser(null);
      }

      // 3) Navigate to dashboard
      navigate('/dashboard', { replace: true });
    } catch (err) {
      setError(err.name === 'AbortError' ? 'Request timed out.' : err.message);
    } finally {
      setPassword(''); // small hygiene
      setIsSubmitting(false);
    }
  };

  const handleResendVerification = async () => {
    if (resending || !username) return;

    setResending(true);
    setResendMessage('');

    try {
      const res = await fetch(`${API_URL}/api/email/resend`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username }),
      });

      const data = await res.json();

      if (res.ok) {
        setResendMessage('Verification email sent! Please check your inbox.');
        setShowResendVerification(false);
      } else {
        setResendMessage(data.error || 'Failed to send verification email.');
      }
    } catch (err) {
      setResendMessage('Network error. Please try again.');
    } finally {
      setResending(false);
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter' && isSubmitting) {
      event.preventDefault();
    }
  };

  return (
    <>
      <h2>Welcome Back</h2>
      <form onSubmit={handleLogin} onKeyDown={handleKeyDown} className="login-form">

        {registrationSuccessMessage && (
          <p className="success-message">{registrationSuccessMessage}</p>
        )}

        {resendMessage && (
          <p className={resendMessage.includes('sent') ? 'success-message' : 'error-message'}>
            {resendMessage}
          </p>
        )}

        <div className="form-group">
          <label htmlFor="username">Username or Email</label>
          <input
            id="username"
            type="text"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            required
            disabled={isSubmitting}
            autoComplete="username"
            autoCapitalize="none"
            style={{ fontSize: '16px' }}
          />
        </div>

        <div className="form-group">
          <label htmlFor="password">Password</label>
          <input
            id="password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            disabled={isSubmitting}
            autoComplete="current-password"
            style={{ fontSize: '16px' }}
          />
        </div>

        {error && <p className="error-message">{error}</p>}

        {showResendVerification && (
          <button
            type="button"
            className="submit-button"
            onClick={handleResendVerification}
            disabled={resending}
            style={{ marginTop: '0.5rem', backgroundColor: '#f59e0b' }}
          >
            {resending ? 'Sending...' : 'Resend Verification Email'}
          </button>
        )}

        <button
          type="submit"
          className="submit-button"
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Logging In...' : 'Login'}
        </button>

        <div className="signup-link" style={{ marginTop: '0.25rem' }}>
          <button
            type="button"
            className="link-button"
            onClick={onForgotPassword}
            disabled={isSubmitting}
          >
            Forgot your password?
          </button>
        </div>

      </form>
    </>
  );
}
